// O ENSAIO DE LAYOUT — "este arquivo passa?", respondido sem consequência nenhuma.
//
// O painel é UM só para os dois perfis de quem opera (averbadora e prefeitura),
// e isso não é economia de código: é a garantia de que a resposta que a
// prefeitura vê é a mesma que a averbadora vê quando ela liga para perguntar
// "por que recusou?". Duas telas divergiriam na primeira mudança de campo.
//
// Quatro decisões moldam o que está aqui:
//
//  1. OS TRÊS VEREDITOS SÃO RESULTADO, NÃO ERRO. `recusado` e `ambíguo` voltam
//     como `200` da API e aparecem como resposta — com o que faltou e o que
//     veio —, nunca como faixa vermelha de falha. O erro de verdade (rede,
//     competência torta, envio grande demais) tem `EstadoErro` próprio.
//  2. AMBÍGUO PEDE UMA ESCOLHA, e não uma explicação. Dois layouts aceitando o
//     mesmo pacote significa que ele não se identifica sozinho; a tela devolve
//     a decisão a quem sabe de qual ente o arquivo é, com um botão por
//     candidato que reenvia forçando aquele layout.
//  3. A PRÉVIA É TEXTO, e curta. Cinquenta linhas por aba respondem "as colunas
//     saíram no lugar?" sem trazer uma folha inteira para dentro do navegador;
//     quem quer o resto baixa o zip, que é montado no servidor.
//  4. NADA FICA GRAVADO. O ensaio não abre folha, não entra na troca de
//     arquivos e não guarda o conteúdo — o único rastro é a linha de registro,
//     que carrega só números (FR-029). Por isso o painel pode ser oferecido
//     para um arquivo de um ente que ainda nem é cliente.

import { useEffect, useId, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  EstadoErro,
  EstadoVazio,
  Pill,
  type Column,
  type PillVariant,
} from "@atlas/ui/web";
import type { EnsaioCandidato, EnsaioResposta } from "@atlas/sdk";
import { atlas } from "../lib/sdk";
import { LayoutChip, ehCodigoPadrao } from "./LayoutChip";
import { RelatorioConversaoPainel } from "../routes/prefeitura/troca-arquivos-relatorio";

// ============================================================ peças

/**
 * Salva um blob com nome de arquivo.
 *
 * A rota do ensaio exige `Authorization`, e `<a href download>` não manda
 * header nenhum — daí o caminho ser "buscar pelo SDK, salvar o blob", e não um
 * link direto. Exportado porque a tela do ensaio faz o mesmo com o movimento.
 */
export function baixarBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revogar na hora cancela o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const VEREDITO: Record<EnsaioResposta["veredito"], { rotulo: string; variant: PillVariant }> = {
  reconhecido: { rotulo: "reconhecido", variant: "aceita" },
  ambiguo: { rotulo: "ambíguo", variant: "pendente" },
  recusado: { rotulo: "recusado", variant: "rejeitada" },
};

/** As três abas da prévia, na ordem em que o pacote padrão é lido. */
const ABAS = [
  { chave: "carga", rotulo: "carga" },
  { chave: "consignados", rotulo: "consignados" },
  { chave: "rubricas", rotulo: "rubricas" },
] as const;
type AbaPacote = (typeof ABAS)[number]["chave"];

const LINHAS_DA_PREVIA = 50;

const rotulo: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: "var(--text-muted)" };
const campo: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 13,
};

/** As primeiras N linhas de um arquivo do pacote, e quantas ficaram de fora. */
function previa(texto: string): { linhas: string; restantes: number } {
  const todas = texto.split("\n");
  const restantes = Math.max(0, todas.length - LINHAS_DA_PREVIA);
  return { linhas: todas.slice(0, LINHAS_DA_PREVIA).join("\n"), restantes };
}

const listaOuTraco = (xs: string[]) =>
  xs.length === 0 ? <span style={{ color: "var(--text-dim)" }}>—</span> : <>{xs.join(", ")}</>;

const COLUNAS_CANDIDATOS: Column<EnsaioCandidato>[] = [
  {
    key: "layout",
    header: "Layout",
    wrap: true,
    render: (c) => (
      <span style={{ display: "grid", gap: 2 }}>
        <strong>{c.nome}</strong>
        <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
          {c.layout}
        </code>
      </span>
    ),
  },
  {
    key: "reconhecido",
    header: "Resultado",
    width: 130,
    render: (c) => (
      <Pill variant={c.reconhecido ? "aceita" : "rejeitada"}>
        {c.reconhecido ? "reconheceu" : "não reconheceu"}
      </Pill>
    ),
  },
  { key: "esperado", header: "Esperado", wrap: true, render: (c) => listaOuTraco(c.esperado) },
  { key: "encontrado", header: "Encontrado", wrap: true, render: (c) => listaOuTraco(c.encontrado) },
  {
    key: "motivo",
    header: "Motivo",
    wrap: true,
    render: (c) => (c.motivo ? <>{c.motivo}</> : <span style={{ color: "var(--text-dim)" }}>—</span>),
  },
];

/** Uma entrada do pacote que nenhum papel do layout consumiu. */
interface EntradaIgnorada {
  nome: string;
}

const COLUNAS_IGNORADAS: Column<EntradaIgnorada>[] = [
  { key: "nome", header: "Entrada do pacote", mono: true, wrap: true },
];

/**
 * O que veio no pacote e o layout reconhecido NÃO usou.
 *
 * Vale a tabela porque é a explicação de um número que não bate sem ninguém ter
 * errado nada: o operador zipou a pasta inteira, o layout leu os dois arquivos
 * que conhece e ignorou o resto em silêncio. Aqui o silêncio some.
 */
function entradasIgnoradas(r: EnsaioResposta): EntradaIgnorada[] {
  if (!r.layout) return [];
  const usados = new Set(r.candidatos.find((c) => c.layout === r.layout!.codigo)?.encontrados ?? []);
  return r.arquivos.filter((a) => !usados.has(a)).map((nome) => ({ nome }));
}

// ============================================================ o painel

export interface OpcaoLayout {
  codigo: string;
  nome: string;
}

export interface EnsaioPainelProps {
  /**
   * `averbadora` escolhe o layout e a unidade de contexto; `prefeitura` não
   * escolhe nem uma coisa nem outra — os candidatos são o layout em vigor
   * nesta unidade e o padrão Atlas, e a unidade sai do login.
   */
  modo: "averbadora" | "prefeitura";
  /** O catálogo de entrada, para o seletor. Só no modo averbadora. */
  layouts?: OpcaoLayout[];
  /** As unidades pagadoras, para o contexto opcional. Só no modo averbadora. */
  unidades?: { id: number; nome: string }[];
  /** No modo prefeitura, a unidade já escolhida (o lote da folha). */
  unidadeId?: number;
  /** A competência inicial do campo — a de referência da tela que abriu o painel. */
  competenciaPadrao?: string;
}

export function EnsaioPainel({
  modo,
  layouts = [],
  unidades = [],
  unidadeId,
  competenciaPadrao,
}: EnsaioPainelProps) {
  const id = useId();
  const idArquivos = `${id}-arquivos`;
  const idLayout = `${id}-layout`;
  const idUnidade = `${id}-unidade`;
  const idCompetencia = `${id}-competencia`;

  const [arquivos, setArquivos] = useState<File[]>([]);
  const [layout, setLayout] = useState("");
  const [unidade, setUnidade] = useState("");
  const [competencia, setCompetencia] = useState(competenciaPadrao ?? "");
  const [aba, setAba] = useState<AbaPacote>("carga");
  const [arrastando, setArrastando] = useState(false);

  // A competência de referência chega DEPOIS do primeiro render (vem com o
  // hub). Sem isto o campo ficaria vazio para sempre e o ensaio rodaria na
  // competência corrente do servidor, que não é necessariamente a que a tela
  // está mostrando. Só preenche o que o operador ainda não digitou.
  useEffect(() => {
    if (competenciaPadrao) setCompetencia((atual) => atual || competenciaPadrao);
  }, [competenciaPadrao]);

  /**
   * O layout vai por parâmetro, e não pelo estado, porque o botão do ambíguo
   * REENVIA forçando um candidato — se ele dependesse de `setLayout` teria de
   * esperar o próximo render para mandar o certo.
   */
  const ensaio = useMutation({
    mutationFn: (forcado?: string) => {
      const escolhido = forcado ?? layout;
      const comp = competencia.trim();
      if (modo === "prefeitura") {
        return atlas.prefeitura.layoutsEnsaiar(arquivos, {
          ...(unidadeId !== undefined ? { unidadeId } : {}),
          ...(comp ? { competencia: comp } : {}),
        });
      }
      return atlas.admin.layouts.ensaiar(arquivos, {
        ...(escolhido ? { layout: escolhido } : {}),
        ...(unidade ? { unidadeId: Number(unidade) } : {}),
        ...(comp ? { competencia: comp } : {}),
      });
    },
    onSuccess: () => setAba("carga"),
  });

  const baixarPacote = useMutation({
    mutationFn: async () => {
      const comp = competencia.trim();
      const codigo = ensaio.data?.layout?.codigo;
      const blob =
        modo === "prefeitura"
          ? await atlas.prefeitura.layoutsEnsaiarZip(arquivos, {
              ...(unidadeId !== undefined ? { unidadeId } : {}),
              ...(comp ? { competencia: comp } : {}),
            })
          : await atlas.admin.layouts.ensaiarZip(arquivos, {
              ...(codigo ? { layout: codigo } : {}),
              ...(unidade ? { unidadeId: Number(unidade) } : {}),
              ...(comp ? { competencia: comp } : {}),
            });
      baixarBlob(blob, `ensaio-${codigo ?? "pacote"}.zip`);
    },
  });

  const receber = (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;
    setArquivos(Array.from(lista));
    ensaio.reset();
  };

  const r = ensaio.data;
  const ignoradas = useMemo(() => (r ? entradasIgnoradas(r) : []), [r]);
  const reconhecidos = r?.candidatos.filter((c) => c.reconhecido) ?? [];

  return (
    // `minmax(0, 1fr)` (T081): a tabela de candidatos tem cinco colunas largas
    // (esperado, encontrado, motivo) e, com a coluna implícita `auto`, ela
    // esticaria o painel e faria a página rolar de lado — inclusive dentro do
    // diálogo da prefeitura, que é onde ele fica mais apertado (FR-038).
    <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {/* ------------------------------------------------------ o que ensaiar */}
      <Card>
        {/* A ÁREA TODA É O RÓTULO: o `<label htmlFor>` cobre a zona inteira, e é
            por isso que arrastar, clicar e navegar por teclado levam ao mesmo
            campo. Um `<div onClick>` com o input escondido daria a mesma
            aparência e nenhuma das três. */}
        <label
          htmlFor={idArquivos}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            receber(e.dataTransfer.files);
          }}
          style={{
            display: "grid",
            gap: 6,
            justifyItems: "center",
            textAlign: "center",
            // O input fica escondido DENTRO da zona (`position: absolute`);
            // sem isto ele se posicionaria contra a página, não contra o rótulo.
            position: "relative",
            padding: 22,
            border: `2px dashed ${arrastando ? "var(--accent, var(--gold-500))" : "var(--border-strong)"}`,
            borderRadius: 12,
            cursor: "pointer",
            background: arrastando ? "color-mix(in srgb, var(--border) 30%, transparent)" : "transparent",
          }}
        >
          <strong style={{ fontSize: 14 }}>Arquivos do ensaio</strong>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Arraste o pacote aqui, ou clique para escolher. Vale um zip, uma planilha ou os arquivos soltos
            enviados juntos.
          </span>
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>.zip · .csv · .txt · .xls · .xlsx</span>
          <input
            id={idArquivos}
            type="file"
            multiple
            accept=".zip,.csv,.txt,.xls,.xlsx"
            onChange={(e) => receber(e.target.files)}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
          />
        </label>

        {arquivos.length > 0 ? (
          <p style={{ margin: "10px 0 0", fontSize: 13 }} aria-live="polite">
            {arquivos.length === 1 ? "1 arquivo escolhido" : `${arquivos.length} arquivos escolhidos`}:{" "}
            <code style={{ fontFamily: "var(--font-mono)" }}>{arquivos.map((a) => a.name).join(", ")}</code>
          </p>
        ) : null}

        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
            marginTop: 12,
          }}
        >
          {modo === "averbadora" ? (
            <>
              <div style={{ display: "grid", gap: 4 }}>
                <label htmlFor={idLayout} style={rotulo}>
                  Layout
                </label>
                <select
                  id={idLayout}
                  style={campo}
                  value={layout}
                  onChange={(e) => setLayout(e.target.value)}
                >
                  <option value="">reconhecer automaticamente</option>
                  {layouts.map((l) => (
                    <option key={l.codigo} value={l.codigo}>
                      {l.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                <label htmlFor={idUnidade} style={rotulo}>
                  Unidade (opcional)
                </label>
                <select
                  id={idUnidade}
                  style={campo}
                  value={unidade}
                  onChange={(e) => setUnidade(e.target.value)}
                >
                  <option value="">sem unidade</option>
                  {unidades.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}
          <div style={{ display: "grid", gap: 4 }}>
            <label htmlFor={idCompetencia} style={rotulo}>
              Competência
            </label>
            <input
              id={idCompetencia}
              style={campo}
              value={competencia}
              placeholder="AAAAMM"
              inputMode="numeric"
              onChange={(e) => setCompetencia(e.target.value)}
            />
          </div>
        </div>

        <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          O ensaio não abre folha, não entra no histórico de trocas e não guarda o conteúdo do arquivo. Fica
          registrado só que aconteceu — quem, quando, tamanho e veredito.
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <Button
            onClick={() => ensaio.mutate(undefined)}
            disabled={arquivos.length === 0 || ensaio.isPending}
          >
            {ensaio.isPending ? "Ensaiando…" : "Ensaiar"}
          </Button>
          {arquivos.length > 0 ? (
            <Button
              variant="ghost"
              onClick={() => {
                setArquivos([]);
                ensaio.reset();
              }}
            >
              Limpar
            </Button>
          ) : null}
        </div>
      </Card>

      {/* ------------------------------------------------------ o resultado */}
      {ensaio.isError ? (
        <EstadoErro
          mensagem={`Não foi possível ensaiar: ${(ensaio.error as Error).message}`}
          onTentarDeNovo={() => ensaio.mutate(undefined)}
        />
      ) : null}

      {r ? (
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Veredito</h3>
            <Pill variant={VEREDITO[r.veredito].variant}>{VEREDITO[r.veredito].rotulo}</Pill>
            {/* A identidade do layout reconhecido, igual à do hub e da ficha
                (FR-036). O nome sai do candidato — a resposta do ensaio guarda
                só código e versão no campo `layout`. */}
            {r.layout ? (
              <LayoutChip
                tamanho="sm"
                nome={r.candidatos.find((c) => c.layout === r.layout!.codigo)?.nome ?? r.layout.codigo}
                codigo={r.layout.codigo}
                versao={r.layout.versao}
                padrao={ehCodigoPadrao(r.layout.codigo)}
              />
            ) : null}
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
              {r.registro.tamanhoBytes.toLocaleString("pt-BR")} bytes
            </span>
          </div>

          {r.veredito === "recusado" ? (
            <p style={{ margin: "0 0 10px", fontSize: 13 }}>
              <b>Nenhum layout reconheceu este pacote.</b>{" "}
              {r.motivo ?? "Compare abaixo o que cada layout esperava com o que veio."}
              {r.cenario ? (
                <>
                  {" "}
                  <code style={{ fontFamily: "var(--font-mono)" }}>{r.cenario}</code>
                </>
              ) : null}
            </p>
          ) : null}

          {r.veredito === "ambiguo" ? (
            <EstadoVazio
              titulo="Mais de um layout reconheceu — escolha um"
              orientacao={
                <span style={{ display: "grid", gap: 10, justifyItems: "center" }}>
                  <span>
                    Este pacote não se identifica sozinho: mais de um layout aceita a forma dele. Diga de qual
                    órgão o arquivo é e o ensaio recomeça contra o layout escolhido.
                  </span>
                  <span style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                    {reconhecidos.map((c) => (
                      <Button
                        key={c.layout}
                        size="sm"
                        variant="ghost"
                        disabled={ensaio.isPending}
                        onClick={() => ensaio.mutate(c.layout)}
                      >
                        Ensaiar como {c.nome}
                      </Button>
                    ))}
                  </span>
                </span>
              }
            />
          ) : null}

          <h4 style={{ margin: "12px 0 6px", fontSize: 13 }}>Layouts tentados</h4>
          <DataTable
            caption="Layouts tentados no ensaio, com o que cada um esperava, o que o pacote trouxe e o motivo da recusa"
            columns={COLUNAS_CANDIDATOS}
            rows={r.candidatos}
            rowKey={(c) => c.layout}
            emptyState={
              <EstadoVazio
                titulo="Nenhum layout foi tentado"
                orientacao="O pacote nem chegou a ser aberto — confira o envio e ensaie de novo."
              />
            }
          />

          {ignoradas.length > 0 ? (
            <>
              <h4 style={{ margin: "14px 0 6px", fontSize: 13 }}>Entradas que o layout não usou</h4>
              <p style={{ margin: "0 0 6px", fontSize: 12, color: "var(--text-muted)" }}>
                Vieram no pacote e nenhum papel do layout as consome. Não é erro — mas é o que explica uma
                contagem menor do que a esperada.
              </p>
              <DataTable
                caption="Entradas do pacote enviado que o layout reconhecido não consumiu"
                columns={COLUNAS_IGNORADAS}
                rows={ignoradas}
                rowKey={(e) => e.nome}
              />
            </>
          ) : null}

          {r.avisos.length > 0 ? (
            <>
              <h4 style={{ margin: "14px 0 6px", fontSize: 13 }}>Avisos</h4>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
                {r.avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>
      ) : null}

      {r?.relatorio ? <RelatorioConversaoPainel relatorio={r.relatorio} /> : null}

      {/* ------------------------------------------------------ a prévia do pacote */}
      {r?.pacote ? (
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Prévia do pacote padrão</h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => baixarPacote.mutate()}
              disabled={baixarPacote.isPending}
            >
              {baixarPacote.isPending ? "Montando…" : "Baixar pacote (zip)"}
            </Button>
          </div>
          <div
            role="tablist"
            aria-label="Arquivos do pacote padrão"
            style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
          >
            {ABAS.map((a) => (
              <button
                key={a.chave}
                // A aba e o quadro que ela mostra se apontam um ao outro
                // (T081): sem `aria-controls`/`aria-labelledby` o leitor de
                // tela anuncia três botões e um bloco de texto solto, e não
                // "prévia de carga".
                id={`${id}-aba-${a.chave}`}
                aria-controls={`${id}-previa`}
                type="button"
                role="tab"
                aria-selected={aba === a.chave}
                onClick={() => setAba(a.chave)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: "1px solid var(--border)",
                  background: aba === a.chave ? "var(--border)" : "transparent",
                  color: "var(--text)",
                  font: "inherit",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {a.rotulo}
              </button>
            ))}
          </div>
          <PreviaDoArquivo id={`${id}-previa`} rotuladoPor={`${id}-aba-${aba}`} texto={r.pacote[aba]} />
          {baixarPacote.isError ? (
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--danger-500, #d33)" }}>
              {(baixarPacote.error as Error).message}
            </p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Cinquenta linhas em monoespaçado, com a rolagem PRESA no quadro (FR-038).
 *
 * `tabIndex={0}` não é enfeite (T081, FR-039): um bloco que rola e não recebe
 * foco esconde o que está abaixo da linha 20 de quem não usa mouse — é o que a
 * regra `scrollable-region-focusable` do axe acusa como falha séria.
 */
function PreviaDoArquivo({ id, rotuladoPor, texto }: { id: string; rotuladoPor: string; texto: string }) {
  const { linhas, restantes } = previa(texto);
  return (
    <>
      <pre
        id={id}
        role="tabpanel"
        aria-labelledby={rotuladoPor}
        tabIndex={0}
        style={{
          margin: "8px 0 0",
          padding: 10,
          maxHeight: 320,
          overflow: "auto",
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--surface)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {linhas || "(vazio)"}
      </pre>
      <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text-dim)" }}>
        {restantes > 0
          ? `Primeiras ${LINHAS_DA_PREVIA} linhas — outras ${restantes.toLocaleString("pt-BR")} ficaram de fora. O zip traz o arquivo inteiro.`
          : "Arquivo inteiro."}
      </p>
    </>
  );
}
